// Dedicated valuation pages for the six highest-value Palworld TCG chase
// printings.
//
// These are deliberately narrower than /cards/$code (which is the card's rules
// and printing record): each page answers a single transactional query — "how
// much is <card> worth" — with an SSR price history graph, a day-by-day recorded
// price table, TCGplayer completed sales, and Product + AggregateOffer +
// PriceSpecification markup so the price basis (asking listing vs completed
// sale) is machine-readable. eBay is asking-only; TCGplayer is the sole
// completed-sale source.
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { priceBoardQueryOptions } from "@/lib/price-board-query";
import { formatMoney, type PriceRow } from "@/lib/prices-shared";
import { OFFICIAL_CARD_BY_CODE, SET_NAMES, type OfficialCard } from "@/lib/tcg-cards";
import { CardImage } from "@/components/CardImage";
import { RarityBadge } from "@/components/RarityBadge";
import { HoverChart } from "@/components/HoverChart";
import { FaqSection, faqSchema, type FaqItem } from "@/components/PageFaq";
import { breadcrumbSchema, productSchema, datasetSchema, snippetAnswerSchema } from "@/lib/schema";
import { SnippetFunnel } from "@/components/SnippetFunnel";
import { PriceFreshness } from "@/components/PriceFreshness";
import { AffiliateDisclosure } from "@/components/AffiliateDisclosure";
import { CrossCategoryLinks } from "@/components/CrossCategoryLinks";
import { cardLinkProps } from "@/lib/card-link";
import { CHASE_VALUE_CARDS, CHASE_VALUE_BY_CODE } from "@/lib/chase-value-cards";
import { ebayAffiliateUrl } from "@/lib/ebay-affiliate";
import { tcgplayerAffiliateUrl } from "@/lib/tcgplayer-affiliate";

const SITE = "https://palstars.com";



function faqsFor(card: OfficialCard): FaqItem[] {
  const label = `${card.name} ${card.code}`;
  return [
    {
      q: `How much is ${label} worth?`,
      a: `The market figure at the top of this page is the depth-weighted median of validated English asking listings for ${label}, rebuilt on every daily sync. Where completed TCGplayer sales exist, the sold average is shown next to it, because a completed sale is money that actually changed hands rather than what a seller hopes to get.`,
    },
    {
      q: `Is the ${card.name} parallel worth more than the base card?`,
      a: `Yes. The base printing and this parallel share the same name, number and rules text, so they play identically in a 50-card deck. Only the parallel is scarce, so the premium is paid for the finish and the print run.`,
    },
    {
      q: `Where can I check ${card.name} sold prices?`,
      a: `TCGplayer is the only marketplace that grants Pal Stars completed-sale access, so the sold column on this page and on the Palworld TCG sold prices page comes from TCGplayer. eBay figures here are active asking listings only — no eBay sold price is shown or estimated.`,
    },
    {
      q: `How often does this ${card.name} price update?`,
      a: `Twice a day, every day. There is no weekly refresh rotation, so a spike after a tournament result or a restock is repriced the same day.`,
    },
    {
      q: `Should I buy ${card.name} raw or graded?`,
      a: `Graded copies are tracked separately and never blended into the raw price on this page. Compare the graded ask against the raw ask and the population report before paying a slab premium, especially on a set this young.`,
    },
  ];
}

export const Route = createFileRoute("/palworld-card-value/$code")({
  beforeLoad: ({ params }) => {
    const upper = params.code.toUpperCase();
    // Unknown code: send visitors and crawlers to the ranking hub instead of a soft 404.
    if (!CHASE_VALUE_BY_CODE.has(upper)) {
      throw redirect({ to: "/most-expensive-palworld-cards" });
    }
    // One indexable URL per card: any other casing redirects to the canonical code.
    if (params.code !== upper) {
      throw redirect({ to: "/palworld-card-value/$code", params: { code: upper } });
    }
  },

  loader: ({ context }) =>
    context.queryClient.ensureQueryData(priceBoardQueryOptions()),
  head: ({ params, loaderData }) => {
    const code = params.code.toUpperCase();
    const card = OFFICIAL_CARD_BY_CODE.get(code);
    const path = `/palworld-card-value/${code}`;
    const url = `${SITE}${path}`;
    // Prices in the markup are only the ones this page visibly renders.
    const row = loaderData?.rows?.find((r) => r.code.toUpperCase() === code);
    if (!card) return { meta: [{ title: "Palworld card value" }, { name: "robots", content: "noindex" }] };
    const title = `${card.name} ${card.code} Value — Palworld TCG Price`;
    const description = `${card.name} (${card.code}, ${card.rarity}) Palworld TCG value: live market price, price history graph, day-by-day recorded prices and TCGplayer completed sales, updated daily.`;
    const q = `How much is ${card.name} ${card.code} worth?`;
    const a = `${card.name} ${card.code} is a ${card.rarity} chase printing from ${SET_NAMES[card.set] ?? card.set}. Its value is set by validated English marketplace asking listings and, where sales are recorded, the TCGplayer completed-sale average — the parallel trades well above the base printing of the same card number because they share rules text but not scarcity. Pal Stars reprices it on every daily sync rather than a weekly rotation.`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
        ...(card.image
          ? [
              { property: "og:image", content: card.image },
              { name: "twitter:image", content: card.image },
            ]
          : []),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        breadcrumbSchema([
          ["Home", "/"],
          ["TCG Price Chart", "/prices"],
          ["Most Expensive Cards", "/most-expensive-palworld-cards"],
          [`${card.name} value`, path],
        ]),
        productSchema({
          name: `${card.name} ${card.code} — Palworld TCG card`,
          description,
          path,
          ...(card.image ? { image: card.image } : {}),
          sku: card.code,
          category: `Palworld OFFICIAL CARD GAME / ${SET_NAMES[card.set] ?? card.set}`,
          lowPrice: row?.low && row.low > 0 ? row.low : (row?.market ?? 0.01),
          ...(row?.high ? { highPrice: row.high } : {}),
          ...(row?.listings ? { offerCount: row.listings } : {}),
          // PriceSpecification per marketplace so the price basis is explicit:
          // eBay is asking-only, TCGplayer sold is a completed sale.
          offers: [
            ...(row?.tcgMarket
              ? [
                  {
                    name: "TCGplayer asking price",
                    price: row.tcgMarket,
                    seller: "TCGplayer",
                    priceType: "Lowest validated TCGplayer asking price for an English raw copy",
                    ...(row.history.at(-1)?.day ? { validFrom: row.history.at(-1)!.day } : {}),
                  },
                ]
              : []),
            ...(row?.tcgSoldAvg
              ? [
                  {
                    name: "TCGplayer completed-sale average",
                    price: row.tcgSoldAvg,
                    seller: "TCGplayer",
                    priceType: `Average of ${row.tcgSoldCount} recent completed TCGplayer sale(s)`,
                    ...(row.soldLastAt ? { validFrom: row.soldLastAt.slice(0, 10) } : {}),
                  },
                ]
              : []),
            ...(row?.ebayMarket
              ? [
                  {
                    name: "eBay asking price",
                    price: row.ebayMarket,
                    seller: "eBay",
                    priceType: "Median validated eBay asking listing (active listings only, not a completed sale)",
                  },
                ]
              : []),
          ],
          properties: [
            { name: "Card number", value: card.code },
            { name: "Rarity", value: card.rarity },
            { name: "Set", value: SET_NAMES[card.set] ?? card.set },
          ],
        }),
        datasetSchema({
          name: `${card.name} ${card.code} Palworld TCG price history`,
          description: `Recorded daily marketplace prices for ${card.name} ${card.code}, split by platform, with TCGplayer completed-sale averages.`,
          path,
          keywords: [
            `${card.name} card value`,
            `${card.code} price`,
            `${card.name} Palworld TCG price`,
            `${card.name} sold price`,
          ],
          variables: ["Market price (USD)", "eBay asking price (USD)", "TCGplayer asking price (USD)", "TCGplayer sold average (USD)"],
        }),
        snippetAnswerSchema({ question: q, answer: a, path }),
        faqSchema(faqsFor(card), url),
      ],
    };
  },
  component: CardValuePage,
});

function CardValuePage() {
  const { code } = Route.useParams();
  const upper = code.toUpperCase();
  const card = OFFICIAL_CARD_BY_CODE.get(upper);
  const angle = CHASE_VALUE_BY_CODE.get(upper)?.angle ?? "";

  const { data } = useQuery(priceBoardQueryOptions());

  const row = useMemo(() => (data?.rows ?? []).find((r) => r.code.toUpperCase() === upper), [data, upper]);

  if (!card) return null;

  const setLabel = SET_NAMES[card.set] ?? card.set;
  const ebayUrl = ebayAffiliateUrl(
    `https://www.ebay.com/sch/i.html?_nkw=${encodeURIComponent(`${card.name} ${card.code} Palworld card`)}`,
    card.code,
  );
  const tcgUrl = tcgplayerAffiliateUrl(
    `https://www.tcgplayer.com/search/all/product?q=${encodeURIComponent(`${card.name} ${card.code}`)}`,
    card.code,
  );

  const history = row?.history ?? [];
  const chartDays = [...new Set([
    ...history.map((p) => p.day),
    ...(row?.tcgHistory ?? []).map((p) => p.day),
    ...(row?.tcgSoldHistory ?? []).map((p) => p.day),
  ])].sort();
  const labels = chartDays.map((day) => day.slice(5));
  const marketByDay = new Map(history.map((p) => [p.day, p.value]));
  const tcgByDay = new Map((row?.tcgHistory ?? []).map((p) => [p.day, p.value]));
  const soldByDay = new Map((row?.tcgSoldHistory ?? []).map((p) => [p.day, p.value]));
  const recordedDays = [...new Set([
    ...(row?.ebayHistory ?? []).map((p) => p.day),
    ...(row?.tcgHistory ?? []).map((p) => p.day),
    ...(row?.tcgSoldHistory ?? []).map((p) => p.day),
  ])].sort().reverse().slice(0, 14);
  const ebayHistoryByDay = new Map((row?.ebayHistory ?? []).map((p) => [p.day, p]));
  const tcgHistoryByDay = new Map((row?.tcgHistory ?? []).map((p) => [p.day, p]));
  const soldHistoryByDay = new Map((row?.tcgSoldHistory ?? []).map((p) => [p.day, p]));

  const q = `How much is ${card.name} ${card.code} worth?`;
  const a = `${card.name} ${card.code} is a ${card.rarity} chase printing from ${setLabel}. Its value comes from validated English asking listings plus the TCGplayer completed-sale average where sales are recorded; the parallel trades far above the base printing of the same card number because they share rules text but not scarcity.`;

  return (
    <main className="mx-auto max-w-5xl px-4 py-10">
      <nav aria-label="Breadcrumb" className="mb-4 text-xs text-muted-foreground">
        <Link to="/" className="hover:text-primary">Home</Link> ·{" "}
        <Link to="/prices" className="hover:text-primary">TCG Price Chart</Link> ·{" "}
        <Link to="/most-expensive-palworld-cards" className="hover:text-primary">Most Expensive Cards</Link> ·{" "}
        <span>{card.name} value</span>
      </nav>

      <div className="grid gap-6 sm:grid-cols-[minmax(0,240px)_1fr]">
        {card.image ? (
          <CardImage
            src={card.image}
            alt={`${card.name} ${card.code} ${card.rarity} Palworld TCG card from ${setLabel}`}
            width={480}
            height={670}
            priority
            responsive
            sizes="(max-width: 640px) 60vw, 240px"
            className="w-full rounded-xl"
          />
        ) : null}
        <header>
          <h1 className="text-3xl font-extrabold sm:text-4xl">
            {card.name} {card.code} Value
          </h1>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
            <RarityBadge code={card.code} />
            <span className="rounded-full border border-border/60 px-2 py-0.5">{setLabel}</span>
            <span className="rounded-full border border-border/60 px-2 py-0.5">{card.color}</span>
            <span className="rounded-full border border-border/60 px-2 py-0.5">{card.type}</span>
          </div>
          <p className="aeo-answer mt-3 text-sm text-muted-foreground">{a}</p>
          {angle ? <p className="mt-2 text-sm text-muted-foreground">{angle}</p> : null}

          <dl className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label="Market (asking)" value={row ? formatMoney(row.market, "USD") : "—"} />
            <Stat
              label="TCGplayer last sold"
              value={row?.tcgSoldAvg ? formatMoney(row.tcgSoldAvg, "USD") : "No sale recorded"}
            />
            <Stat label="Lowest ask" value={row ? formatMoney(row.low, "USD") : "—"} />
            <Stat
              label="7-day move"
              value={row ? `${row.change7d > 0 ? "+" : ""}${row.change7d.toFixed(1)}%` : "—"}
            />
          </dl>

          <div className="mt-4 flex flex-wrap gap-2 text-sm">
            <a
              href={tcgUrl}
              rel="nofollow sponsored noopener noreferrer"
              target="_blank"
              className="rounded-lg border border-primary/60 px-3 py-2 font-semibold hover:bg-primary/10"
            >
              Buy on TCGplayer
            </a>
            <a
              href={ebayUrl}
              rel="nofollow sponsored noopener noreferrer"
              target="_blank"
              className="rounded-lg border border-border/60 px-3 py-2 hover:border-primary/60"
            >
              Buy on eBay
            </a>
          </div>
        </header>
      </div>

      <PriceFreshness updated={data?.updated ?? null} />

      <SnippetFunnel
        className="mt-6"
        question={q}
        answer={a}
        withheld={[
          `Today's ${card.name} market price`,
          "TCGplayer completed-sale average",
          "Day-by-day recorded price history",
          "Graded vs raw asking comparison",
        ]}
        cta={{ to: "/prices", label: "Open the full Palworld TCG price chart" }}
      />

      <section className="mt-8" aria-labelledby="graph-heading">
        <h2 id="graph-heading" className="text-xl font-bold">
          {card.name} {card.code} price history
        </h2>
        {chartDays.length > 1 ? (
          <div className="glass mt-3 rounded-2xl p-3">
            <HoverChart
              labels={labels}
              format={(v) => formatMoney(v, "USD")}
              ariaLabel={`${card.name} ${card.code} Palworld TCG price history in US dollars`}
              area
              series={[
                {
                  id: "market",
                  label: "Market (asking)",
                  color: "hsl(152 70% 50%)",
                  values: chartDays.map((day) => marketByDay.get(day)),
                },
                ...(row && row.tcgHistory.length > 0
                  ? [
                      {
                        id: "tcg",
                        label: "TCGplayer asking",
                        color: "hsl(210 90% 66%)",
                        // Aligned to the same day axis as the market line, so the
                        // TCGplayer series sits on its real dates instead of
                        // starting at the left edge and stopping short.
                        values: chartDays.map((day) => tcgByDay.get(day)),
                      },
                    ]
                  : []),
                ...(row && row.tcgSoldHistory.length > 0
                  ? [
                      {
                        id: "tcg-sold",
                        label: "TCGplayer sold avg",
                        color: "hsl(42 92% 58%)",
                        values: chartDays.map((day) => soldByDay.get(day)),
                      },
                    ]
                  : []),
              ]}
            />
          </div>
        ) : (
          <p className="mt-2 text-sm text-muted-foreground">
            Price history starts building on the first daily sync that records a validated listing for this printing.
          </p>
        )}
      </section>

      <section className="mt-8" aria-labelledby="sales-heading">
        <h2 id="sales-heading" className="text-xl font-bold">
          Recorded {card.name} prices and completed sales
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          One row per recorded sync day. eBay and TCGplayer columns are asking prices; the completed-sale line below the
          table is TCGplayer only.
        </p>
        <div className="glass mt-3 overflow-x-auto rounded-2xl">
          <table className="w-full min-w-[520px] text-sm">
            <caption className="sr-only">
              Recorded daily marketplace prices for {card.name} {card.code}
            </caption>
            <thead className="text-xs uppercase text-muted-foreground">
              <tr className="border-b border-border/60">
                <th scope="col" className="px-3 py-2 text-left">Day (UTC)</th>
                <th scope="col" className="px-3 py-2 text-right">eBay ask</th>
                <th scope="col" className="px-3 py-2 text-right">TCGplayer ask</th>
                <th scope="col" className="px-3 py-2 text-right">TCGplayer sold</th>
                <th scope="col" className="px-3 py-2 text-right">Sales</th>
              </tr>
            </thead>
            <tbody>
              {recordedDays.length > 0 ? (
                recordedDays.map((day) => {
                  const ebay = ebayHistoryByDay.get(day);
                  const tcg = tcgHistoryByDay.get(day);
                  const sold = soldHistoryByDay.get(day);
                  return (
                  <tr key={day} className="border-b border-border/40 last:border-0">
                    <td className="px-3 py-2 tabular-nums">{day}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{ebay ? formatMoney(ebay.value, "USD") : "—"}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{tcg ? formatMoney(tcg.value, "USD") : "—"}</td>
                    <td className="px-3 py-2 text-right font-semibold tabular-nums">{sold ? formatMoney(sold.value, "USD") : "—"}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{sold?.count ?? "—"}</td>
                  </tr>
                  );
                })
              ) : (
                <tr>
                  <td className="px-3 py-3 text-muted-foreground" colSpan={5}>
                    No recorded sync day yet for this printing — the next daily sync writes the first row.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <p className="mt-2 text-sm text-muted-foreground">
          TCGplayer completed sales:{" "}
          {row?.tcgSoldAvg
            ? `${formatMoney(row.tcgSoldAvg, "USD")} average across ${row.tcgSoldCount} validated sale(s)${row.soldLastAt ? `, newest ${row.soldLastAt.slice(0, 10)}` : ""}.`
            : "no completed sale recorded yet for this printing."}{" "}
          eBay does not grant Pal Stars sold-data access, so no eBay sold figure is shown or estimated.
        </p>
      </section>

      <section className="mt-8" aria-labelledby="related-heading">
        <h2 id="related-heading" className="text-lg font-bold">
          Related Palworld valuation pages
        </h2>
        <ul className="mt-2 space-y-1 text-sm">
          <li>
            <Link {...cardLinkProps(card.code)} className="text-primary hover:underline">
              {card.name} card details and printings
            </Link>
          </li>
          <li>
            <Link to="/most-expensive-palworld-cards" className="text-primary hover:underline">
              Most expensive Palworld TCG cards
            </Link>
          </li>
          <li>
            <Link to="/palworld-alternate-art-price-guide" className="text-primary hover:underline">
              Palworld TCG alternate art price guide
            </Link>
          </li>
          <li>
            <Link to="/tcg-sold-prices" className="text-primary hover:underline">
              Palworld TCG sold prices (TCGplayer completed sales)
            </Link>
          </li>
          <li>
            <Link to="/tcg-price-index" className="text-primary hover:underline">
              Palworld TCG Price Index
            </Link>
          </li>
        </ul>
        <p className="mt-3 text-sm text-muted-foreground">
          Other chase valuations:{" "}
          {CHASE_VALUE_CARDS.filter((c) => c.code.toUpperCase() !== upper).map((c, i) => {
            const other = OFFICIAL_CARD_BY_CODE.get(c.code.toUpperCase());
            return (
              <span key={c.code}>
                {i > 0 ? " · " : ""}
                <Link
                  to="/palworld-card-value/$code"
                  params={{ code: c.code }}
                  className="text-primary hover:underline"
                >
                  {other?.name ?? c.code} value
                </Link>
              </span>
            );
          })}
        </p>
      </section>

      <div className="mt-6">
        <AffiliateDisclosure />
      </div>

      <FaqSection items={faqsFor(card)} />
      <CrossCategoryLinks side="tcg" />
    </main>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-border/60 bg-card/40 p-3">
      <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 text-sm font-bold tabular-nums">{value}</dd>
    </div>
  );
}

export type { PriceRow };
